import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Loader2, Send, Paperclip, FileBox, X } from "lucide-react";
import { toast } from "sonner";

const BK = process.env.REACT_APP_BACKEND_URL;

function AttachmentView({ f }) {
    const url = `${BK}/api/connect/chat-files/${f.id}`;
    if (f.is_image) {
        return (
            <a href={url} target="_blank" rel="noreferrer" className="block mt-1.5" data-testid={`chat-img-${f.id}`}>
                <img src={url} alt={f.name} className="max-h-40 rounded-lg border border-slate-200 dark:border-slate-700" />
            </a>
        );
    }
    return (
        <a href={url} target="_blank" rel="noreferrer" data-testid={`chat-file-${f.id}`}
            className="inline-flex items-center gap-1.5 mt-1.5 px-2.5 py-1.5 rounded-lg bg-white/70 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 text-[12px] text-slate-700 dark:text-slate-300 hover:border-cyan-500 transition-colors">
            <FileBox size={12} /> {f.name} <span className="text-slate-400">{(f.size / 1024 / 1024).toFixed(1)} MB</span>
        </a>
    );
}

export default function ConnectionChat({ base, headers, mySide, onRead }) {
    const [msgs, setMsgs] = useState(null);
    const [text, setText] = useState("");
    const [sending, setSending] = useState(false);
    const [pending, setPending] = useState([]);
    const [uploading, setUploading] = useState(false);
    const endRef = useRef(null);
    const fileRef = useRef(null);
    const filesUrl = base.replace(/\/messages$/, "/files");

    const load = useCallback(async () => {
        try {
            const r = await axios.get(base, { headers });
            setMsgs(r.data.messages || []);
            if (onRead) onRead();
        } catch { /* thread refresh is best-effort */ }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [base]);

    useEffect(() => {
        load();
        const iv = setInterval(load, 15000);
        return () => clearInterval(iv);
    }, [load]);

    useEffect(() => { endRef.current?.scrollIntoView({ block: "nearest" }); }, [msgs]);

    const upload = async (list) => {
        if (!list?.length) return;
        setUploading(true);
        for (const f of Array.from(list).slice(0, 5)) {
            try {
                const fd = new FormData();
                fd.append("file", f);
                const r = await axios.post(filesUrl, fd, { headers });
                setPending((p) => [...p, r.data]);
            } catch (e) { toast.error(e?.response?.data?.detail || `Upload failed: ${f.name}`); }
        }
        setUploading(false);
    };

    const send = async () => {
        if ((!text.trim() && pending.length === 0) || sending || uploading) return;
        setSending(true);
        try {
            await axios.post(base, { text, file_ids: pending.map((f) => f.id) }, { headers });
            setText("");
            setPending([]);
            await load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Message failed"); }
        setSending(false);
    };

    if (msgs === null) return <div className="py-6 text-center"><Loader2 size={16} className="animate-spin mx-auto text-slate-400" /></div>;

    return (
        <div data-testid="connection-chat"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); upload(e.dataTransfer.files); }}>
            <div className="space-y-2 mb-3 max-h-64 overflow-y-auto pr-1">
                {msgs.length === 0 && (
                    <div className="text-center py-5 text-slate-400 text-[12.5px]" data-testid="chat-empty">
                        No messages yet — say hello, or drop a photo / scan file to ask a quick pre-case question.
                    </div>
                )}
                {msgs.map((m) => (
                    <div key={m.id} data-testid={`chat-msg-${m.id}`}
                        className={`max-w-md rounded-xl px-3.5 py-2.5 text-[13px] ${m.from === mySide ? "bg-cyan-50 dark:bg-cyan-900/30 text-slate-800 dark:text-slate-200 ms-auto" : "bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-200"}`}>
                        <div className="text-[11px] text-slate-400 mb-0.5">{m.author} · {new Date(m.at).toLocaleString()}</div>
                        {m.text}
                        {(m.files || []).map((f) => <AttachmentView key={f.id} f={f} />)}
                    </div>
                ))}
                <div ref={endRef} />
            </div>
            {pending.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-2" data-testid="chat-pending-files">
                    {pending.map((f) => (
                        <span key={f.id} data-testid={`chat-pending-${f.id}`}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-slate-100 dark:bg-slate-800 text-[12px] text-slate-700 dark:text-slate-300">
                            <FileBox size={12} /> {f.name} <span className="text-slate-400">{(f.size / 1024 / 1024).toFixed(1)} MB</span>
                            <button onClick={() => setPending((p) => p.filter((x) => x.id !== f.id))} className="text-slate-400 hover:text-rose-500" aria-label={`Remove ${f.name}`}><X size={11} /></button>
                        </span>
                    ))}
                </div>
            )}
            <div className="flex gap-2">
                <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="chat-attach-btn" aria-label="Attach a file"
                    className="px-3.5 rounded-xl border border-slate-300 dark:border-slate-600 text-slate-500 dark:text-slate-400 hover:border-cyan-500 hover:text-cyan-600 transition-colors">
                    {uploading ? <Loader2 size={14} className="animate-spin" /> : <Paperclip size={14} />}
                </button>
                <input ref={fileRef} type="file" multiple hidden data-testid="chat-attach-input"
                    accept=".jpg,.jpeg,.png,.webp,.heic,.stl,.ply,.obj,.dcm,.zip,.pdf"
                    onChange={(e) => { upload(e.target.files); e.target.value = ""; }} />
                <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
                    maxLength={2000} data-testid="chat-input" placeholder="Write a message… (or drop a photo / STL here)"
                    className="flex-1 px-4 py-2.5 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 focus:border-cyan-500 outline-none text-[13.5px]" />
                <button onClick={send} disabled={sending || uploading} data-testid="chat-send"
                    className="px-4 rounded-xl bg-slate-900 dark:bg-cyan-600 text-white disabled:bg-slate-400" aria-label="Send message">
                    {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                </button>
            </div>
        </div>
    );
}
